import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaMotorcycle, FaCar, FaStar, FaMapMarkerAlt, FaChevronRight } from 'react-icons/fa';
import { getNearbyDrivers } from '../../../DELIVERY/services/deliveryServices';
import { DEFAULT_MAP_CENTER, TRACKING_STATUSES } from '../../../../constants';
import { 
    DRIVER_CATEGORIES, 
    CATEGORY_TABS, 
    categorizeDrivers, 
    getDriverBadges 
} from './driverCategorizationUtils';
import './ShiftDriversList.css';

const ShiftDriversList = ({ pickupPos, selectedDriver, onSelectDriver }) => {
    const navigate = useNavigate();
    const [drivers, setDrivers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState(DRIVER_CATEGORIES.ALL);

    useEffect(() => {
        let isMounted = true;
        const fetchShiftDrivers = async () => {
            try {
                const center = pickupPos || { lat: DEFAULT_MAP_CENTER[0], lng: DEFAULT_MAP_CENTER[1] };
                const data = await getNearbyDrivers(center.lat, center.lng, 10);
                if (isMounted) {
                    setDrivers(data || []);
                }
            } catch (err) {
                console.error("Error cargando conductores de turno:", err);
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        fetchShiftDrivers();
        const interval = setInterval(fetchShiftDrivers, 15000);
        return () => {
            isMounted = false;
            clearInterval(interval);
        };
    }, [pickupPos]);

    const displayedDrivers = useMemo(() => {
        return categorizeDrivers(drivers, selectedCategory);
    }, [drivers, selectedCategory]);

    if (loading) {
        return (
            <div className="shift-drivers-container mb-4">
                <div className="shift-drivers-header">
                    <span className="shift-drivers-title">Conductores de turno</span>
                </div>
                <div className="shift-driver-card skeleton-loading p-3 rounded-2xl mb-2">
                    <div className="d-flex align-items-center gap-3">
                        <div className="skeleton-avatar" style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }}></div>
                        <div className="flex-fill">
                            <div className="skeleton-line mb-2" style={{ width: '60%', height: 14, background: 'rgba(255,255,255,0.1)', borderRadius: 4 }}></div>
                            <div className="skeleton-line" style={{ width: '40%', height: 10, background: 'rgba(255,255,255,0.06)', borderRadius: 4 }}></div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="shift-drivers-container mb-4">
            <div className="shift-drivers-header">
                <div className="d-flex align-items-center gap-2">
                    <span className="shift-drivers-title">Conductores de turno</span>
                    <span className="shift-drivers-count">{drivers.length} activos</span>
                </div>
                <button 
                    className="shift-drivers-action-btn"
                    onClick={() => navigate('/solicitar-servicio')}
                >
                    Pedir viaje
                </button>
            </div>

            {/* Píldoras de Categorías (Tabs) */}
            <div className="shift-categories-tabs" role="tablist" aria-label="Categorías de conductores">
                {CATEGORY_TABS.map((tab) => {
                    const isActive = selectedCategory === tab.id;
                    return (
                        <button
                            key={tab.id}
                            type="button"
                            role="tab"
                            aria-selected={isActive}
                            className={`shift-category-pill ${isActive ? 'active' : ''}`}
                            onClick={() => setSelectedCategory(tab.id)}
                        >
                            <span className="shift-category-label">{tab.label}</span>
                        </button>
                    );
                })}
            </div>

            {displayedDrivers.length === 0 ? (
                <div className="shift-empty-card">
                    <FaMotorcycle size={24} className="mb-2 text-muted" />
                    <p className="mb-0 text-secondary" style={{ fontSize: '0.85rem' }}>
                        No hay conductores disponibles en la categoría seleccionada.
                    </p>
                </div>
            ) : (
                <div className="shift-drivers-list">
                    {displayedDrivers.map((driver) => {
                        const isSelected = selectedDriver?.driver_uid === driver.driver_uid;
                        const isOnline = driver.status === TRACKING_STATUSES.ONLINE;
                        const badges = getDriverBadges(driver, drivers);

                        return (
                            <div 
                                key={driver.driver_uid || driver.id}
                                className={`shift-driver-card ${isSelected ? 'selected' : ''}`}
                                onClick={() => onSelectDriver && onSelectDriver(driver)}
                            >
                                <div className="shift-driver-avatar-wrapper">
                                    {driver.avatar_url ? (
                                        <img src={driver.avatar_url} alt={driver.name} className="shift-driver-avatar" />
                                    ) : (
                                        <div className="shift-driver-avatar-fallback">
                                            {driver.vehicle_type === 'carro' ? <FaCar size={18} /> : <FaMotorcycle size={18} />}
                                        </div>
                                    )}
                                    <span className={`shift-status-dot ${isOnline ? 'online' : 'busy'}`}></span>
                                </div>

                                <div className="shift-driver-info">
                                    <div className="shift-driver-top">
                                        <span className="shift-driver-name">{driver.name || 'Conductor Bryemma'}</span>
                                        <span className={`shift-driver-status-badge ${isOnline ? 'badge-online' : 'badge-busy'}`}>
                                            {isOnline ? 'En turno' : 'Ocupado'}
                                        </span>
                                    </div>

                                    {/* Insignias / Badges destacados */}
                                    <div className="shift-driver-badges-row">
                                        {badges.map((b) => (
                                            <span 
                                                key={b.type} 
                                                className={`driver-badge chip-${b.type}`}
                                                style={{ background: b.bg, color: b.color, border: b.border }}
                                            >
                                                {b.label}
                                            </span>
                                        ))}
                                    </div>

                                    <div className="shift-driver-meta">
                                        <span className="shift-driver-detail">
                                            <FaMapMarkerAlt size={10} style={{ color: 'var(--bry-accent)' }} /> 
                                            {driver.distance_km != null ? `${driver.distance_km.toFixed(1)} km` : 'Cerca de ti'}
                                        </span>
                                        {driver.rating && (
                                            <span className="shift-driver-rating">
                                                <FaStar size={10} color="#f59e0b" />
                                                {driver.rating.toFixed(1)}
                                            </span>
                                        )}
                                        {driver.total_trips != null && (
                                            <span className="shift-driver-trips text-muted" style={{ fontSize: '0.75rem' }}>
                                                • {driver.total_trips} viajes
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <FaChevronRight size={14} className="shift-driver-arrow" />
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ShiftDriversList;
